export type OperationKeySlot = { fingerprint: string; key: string } | null;
export type OperationKeyRef = { current: OperationKeySlot };
type OperationKeyStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function browserSessionStorage(): OperationKeyStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function operationKeyFor(ref: OperationKeyRef, fingerprint: string): string {
  if (ref.current?.fingerprint === fingerprint) return ref.current.key;
  const key = crypto.randomUUID();
  ref.current = { fingerprint, key };
  return key;
}

/** Clear only after the caller has validated a terminal API response. */
export function clearOperationKey(ref: OperationKeyRef): void {
  ref.current = null;
}

/**
 * Separates browser-persisted pending operations by authenticated principal.
 * The server remains authoritative for principal scoping; this avoids a later
 * sign-in in the same browser session reusing a previous user's pending key.
 */
export function userScopedOperationStorageKey(operation: string, userId: string, scope?: string): string {
  return scope ? `${operation}.${userId}.${scope}` : `${operation}.${userId}`;
}

/**
 * Keeps a pending financial operation key through a reload after an ambiguous
 * network failure. Session storage is deliberately per-browser-session; the
 * server still scopes the durable key to the authenticated principal.
 */
export function persistedOperationKeyFor(
  ref: OperationKeyRef,
  fingerprint: string,
  storageKey: string,
  storage: OperationKeyStorage | null = browserSessionStorage(),
): string {
  if (!ref.current && storage) {
    try {
      const raw = storage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) as unknown : null;
      const slot = parsed as Partial<Exclude<OperationKeySlot, null>>;
      if (
        parsed != null &&
        typeof parsed === "object" &&
        typeof slot.fingerprint === "string" &&
        typeof slot.key === "string"
      ) {
        ref.current = slot as Exclude<OperationKeySlot, null>;
      }
    } catch {
      try {
        storage.removeItem(storageKey);
      } catch {
        // Browser storage can be entirely unavailable (for example, under a
        // privacy policy). Retain the in-memory retry key in that case.
      }
    }
  }

  const key = operationKeyFor(ref, fingerprint);
  try {
    storage?.setItem(storageKey, JSON.stringify(ref.current));
  } catch {
    // Storage is an availability enhancement only; the in-memory key remains
    // safe for retries in the current page when browser storage is unavailable.
  }
  return key;
}

export function clearPersistedOperationKey(
  ref: OperationKeyRef,
  storageKey: string,
  storage: OperationKeyStorage | null = browserSessionStorage(),
): void {
  clearOperationKey(ref);
  try {
    storage?.removeItem(storageKey);
  } catch {
    // Best effort only; a stale key remains server-principal-scoped.
  }
}

export function isTerminalMutationFailure(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429;
}
